import { Trans } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { NavStepper } from '../../components/steppers/nav-stepper/NavStepper';
import { NavStepperStep } from '../../components/steppers/nav-stepper/NavStepperStep';
import { AgStepMeeting } from './components/step/StepMeeting';
import { useAuthorizationSuspenseQuery } from './Authorization.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { AgStepLanguages } from './components/step/StepLanguage';
import { AgStepValidThru } from './components/step/StepValidThru';
import { AgStepSplit } from './components/step/StepSplit';
import { AgStepShareLimits } from './components/step/StepShareLimit';
import { AgStepBoardStatement } from './components/step/StepBoardStatement';
import { AgStepAuthorization } from './components/step/StepAuthorization';
import { GqlAuthKind, GqlSeverity } from '../../graphql/base-types.graphql';
import { AgStepRegForm } from './components/step/StepRegForm';
import { AgStepDocument } from './components/step/StepDocument';

export const AuthorizationGroupEdit = ({ authorizationGroupId }: { authorizationGroupId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { authorizationGroup, authorizationGroupErrors, companySubscription, meetings, company },
  } = useAuthorizationSuspenseQuery({
    variables: { companyId, authorizationGroupId },
    fetchPolicy: 'cache-and-network',
  });

  const errors = authorizationGroupErrors;
  const meeting = authorizationGroup.meetingConcern?.meeting;

  return (
    <Stack gap={3}>
      <Typography level="h3">
        <Trans id="Authorization" />
      </Typography>

      <NavStepper onStepSaved={() => null} path="*">
        <NavStepperStep path="decision" index label={<Trans id="Decision" />} validatorStatus={errors.decision}>
          <AgStepMeeting
            authorizationGroup={authorizationGroup}
            hasActiveSubscription={companySubscription?.isActive ?? false}
            meeting={meeting}
          />
        </NavStepperStep>

        <NavStepperStep path="language" label={<Trans id="Language" />} validatorStatus={errors.language}>
          <AgStepLanguages authorizationGroup={authorizationGroup} />
        </NavStepperStep>

        <NavStepperStep path="valid-through" label={<Trans id="Valid Through" />} validatorStatus={errors.availability}>
          <AgStepValidThru authorizationGroup={authorizationGroup} agmMeetings={meetings.models} company={company} />
        </NavStepperStep>

        <NavStepperStep
          path="split"
          label={<Trans id="Split" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <AgStepSplit authorizationGroup={authorizationGroup} />
        </NavStepperStep>

        <NavStepperStep
          path="share-limits"
          label={<Trans id="Share Limits" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <AgStepShareLimits authorizationGroup={authorizationGroup} company={company} />
        </NavStepperStep>

        <NavStepperStep
          path="board-statement"
          label={<Trans id="Board Statement" />}
          validatorStatus={errors.boardStatement}
        >
          <AgStepBoardStatement authorizationGroup={authorizationGroup} />
        </NavStepperStep>

        <NavStepperStep path="share-issue" label={<Trans id="Share Issue" />} validatorStatus={errors.auths}>
          <AgStepAuthorization authorizationGroup={authorizationGroup} kind={GqlAuthKind.Issue} />
        </NavStepperStep>

        <NavStepperStep path="warrants" label={<Trans id="Warrants" />} validatorStatus={errors.auths}>
          <AgStepAuthorization authorizationGroup={authorizationGroup} kind={GqlAuthKind.Warrants} />
        </NavStepperStep>

        <NavStepperStep path="convertibles" label={<Trans id="Convertibles" />} validatorStatus={errors.auths}>
          <AgStepAuthorization authorizationGroup={authorizationGroup} kind={GqlAuthKind.Convertibles} />
        </NavStepperStep>

        <NavStepperStep
          path="registration-form"
          label={<Trans id="Registration Form" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <AgStepRegForm authorizationGroup={authorizationGroup} />
        </NavStepperStep>

        <NavStepperStep
          path="documents"
          label={<Trans id="Documents" />}
          validatorStatus={{
            completed: Boolean(authorizationGroup.latestDocument),
            hasError: false,
            severity: GqlSeverity.Warning,
          }}
        >
          <AgStepDocument
            authorizationGroup={authorizationGroup}
            canUseProFeatures={companySubscription?.canUseProFeatures ?? false}
            validatorErrors={errors}
          />
        </NavStepperStep>
      </NavStepper>
    </Stack>
  );
};
